import {
  customInstanceWithHeader,
  type ResponseWithHeaders,
} from '@fukuri-admins/apis/client/base/axiosInstance';
import {
  getGetApiV1ThreeLetterCodePartnerAdminsClientContractsKey,
  getGetApiV1ThreeLetterCodePartnerAdminsClientContractsUrl,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientContracts200,
  GetApiV1ThreeLetterCodePartnerAdminsClientContractsParams,
} from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import type { KeyedMutator } from 'swr';

const fetcher = (
  threeLetterCode: string,
  apiParams: GetApiV1ThreeLetterCodePartnerAdminsClientContractsParams
) =>
  customInstanceWithHeader<GetApiV1ThreeLetterCodePartnerAdminsClientContracts200>(
    getGetApiV1ThreeLetterCodePartnerAdminsClientContractsUrl(threeLetterCode, apiParams),
    { method: 'GET' }
  );

type Args = {
  /** パートナーの 3 文字コード */
  threeLetterCode: string;
  /** 利用企業名の部分一致検索（検索ボタン押下時のみ渡す） */
  clientName?: string;
  /** 契約プラン名の部分一致検索（検索ボタン押下時のみ渡す） */
  contractPlanName?: string;
  /** ページ番号（文字列。undefined のとき API のデフォルト（1ページ目）を使用する） */
  page?: string;
};

type ReturnType = {
  /** 企業契約期間一覧データ */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientContracts200;
  /** 総ページ数 */
  totalPage: number;
  /** 総件数 */
  totalCount: number;
  /** SWR の mutate 関数 */
  mutate: KeyedMutator<ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsClientContracts200>>;
};

type IUseGetPartnerAdminsClientContracts = (args: Args) => ReturnType;

/**
 * パートナー管理者向け企業契約期間一覧取得フック
 *
 * - client_name クエリパラメータで利用企業名の部分一致検索をサポート
 * - contract_plan_name クエリパラメータで契約プラン名の部分一致検索をサポート
 * - page クエリパラメータでページネーション対応
 * - ページネーションメタ（totalPage / totalCount）は pagy のレスポンスヘッダーから取得する
 * - Suspense を使用してデータを取得する
 */
export const useGetPartnerAdminsClientContracts: IUseGetPartnerAdminsClientContracts = ({
  threeLetterCode,
  clientName,
  contractPlanName,
  page,
}) => {
  const apiParams: GetApiV1ThreeLetterCodePartnerAdminsClientContractsParams = {
    ...(clientName && { client_name: clientName }),
    ...(contractPlanName && { contract_plan_name: contractPlanName }),
    ...(page && !Number.isNaN(Number(page)) && { page: Number(page) }),
  };

  const { data, mutate } = useFetch<
    ResponseWithHeaders<GetApiV1ThreeLetterCodePartnerAdminsClientContracts200>
  >(
    getGetApiV1ThreeLetterCodePartnerAdminsClientContractsKey(threeLetterCode, apiParams),
    () => fetcher(threeLetterCode, apiParams),
    { suspense: true }
  );

  const totalPage = Number(data.headers['total-pages']) || 0;
  const totalCount = Number(data.headers['total-count']) || 0;

  return {
    data: data.data,
    totalPage,
    totalCount,
    mutate,
  };
};
